// ============================================================================
// AI TRADE ENGINE v3 — Real age-based values, season sim only
// 17-25 trades/season, 8% blockbuster, never touches user team
// ============================================================================
import { getPlayerRating } from './playerRatings';
import { getPlayerAgeSafe } from './playerAges';

// ============================================================================
// PLAYER TRADE VALUES (internal only — not shown to users)
// ============================================================================
export function getPlayerTradeValue(player, seasonYear) {
  const ovr = getPlayerRating(player.name) || 70;
  const sal = player.salary2526 || player.salary || 0;
  if (player.isDead || sal <= 0) return 0;

  // Base value from OVR (exponential — stars WAY more valuable)
  let value;
  if (ovr >= 92) value = 90 + (ovr - 92) * 8;       // 90-150+ (superstars)
  else if (ovr >= 88) value = 65 + (ovr - 88) * 6.25; // 65-90
  else if (ovr >= 85) value = 48 + (ovr - 85) * 5.67; // 48-65
  else if (ovr >= 82) value = 35 + (ovr - 82) * 4.33; // 35-48
  else if (ovr >= 78) value = 22 + (ovr - 78) * 3.25; // 22-35
  else if (ovr >= 75) value = 14 + (ovr - 75) * 2.67; // 14-22
  else if (ovr >= 72) value = 7 + (ovr - 72) * 2.33;  // 7-14
  else if (ovr >= 68) value = 2 + (ovr - 68) * 1.25;  // 2-7
  else value = Math.max(0, (ovr - 62) * 0.33);         // 0-2

  // REAL age modifier
  const age = getPlayerAgeSafe(player.name, sal, seasonYear || 2025);
  if (age <= 21) value *= 1.4;       // elite youth premium
  else if (age <= 23) value *= 1.25;  // young star
  else if (age <= 25) value *= 1.12;  // ascending
  else if (age <= 27) value *= 1.0;   // prime
  else if (age <= 29) value *= 0.82;  // starting decline
  else if (age <= 31) value *= 0.6;   // significant decline
  else if (age <= 33) value *= 0.35;  // aging vet
  else value *= 0.15;                  // end of career

  // Contract modifier
  const salM = sal / 1e6;
  if (salM > 45 && ovr < 88) value *= 0.35;
  else if (salM > 35 && ovr < 85) value *= 0.5;
  else if (salM > 25 && ovr < 80) value *= 0.6;
  else if (salM > 18 && ovr < 76) value *= 0.7;
  // Cheap good players bonus
  if (salM < 5 && ovr >= 75) value *= 1.3;
  if (salM < 10 && ovr >= 80) value *= 1.15;

  return Math.round(Math.max(0, Math.min(150, value)) * 10) / 10;
}

export function getPickTradeValue(round, pickNum, year, currentYear) {
  const yearsOut = Math.max(0, (year || currentYear) - currentYear);
  let base;
  if (round === 1) {
    if (pickNum && pickNum <= 3) base = 80;
    else if (pickNum && pickNum <= 5) base = 60;
    else if (pickNum && pickNum <= 10) base = 45;
    else if (pickNum && pickNum <= 15) base = 30;
    else if (pickNum && pickNum <= 20) base = 22;
    else base = 16;
  } else {
    base = pickNum && pickNum <= 35 ? 8 : 5;
  }
  return Math.round(base * Math.pow(0.85, yearsOut) * 10) / 10;
}

function analyzeTeam(teamData, seasonYear) {
  if (!teamData?.players) return { avgOvr: 70, payroll: 0, playerCount: 0, activePlayers: [] };
  const activePlayers = teamData.players.filter(p => !p.isDead && (p.salary2526 || 0) > 0);
  let totalOvr = 0;
  for (const p of activePlayers) totalOvr += getPlayerRating(p.name) || 70;
  const avgOvr = activePlayers.length > 0 ? totalOvr / activePlayers.length : 70;
  const payroll = activePlayers.reduce((s, p) => s + (p.salary2526 || 0), 0);
  return { avgOvr, payroll, isOverCap: payroll > 141e6, playerCount: activePlayers.length, activePlayers };
}

function checkSalaryMatch(sending, receiving, senderPayroll) {
  // Under salary cap — no matching needed
  if (senderPayroll <= 141e6) return true;
  // Zero outgoing — can receive small amount via exceptions
  if (sending === 0) return receiving < 10e6;
  // Standard matching: receiving must be within 75-150% of sending (relaxed for AI)
  const ratio = receiving / sending;
  return ratio >= 0.65 && ratio <= 1.5;
}

export function generateAITrades(salaryData, userTeam, seasonYear) {
  const TEAMS = Object.keys(salaryData).filter(t => t !== userTeam && salaryData[t]?.players?.length >= 8);
  if (TEAMS.length < 4) return [];
  const baseTrades = 4 + Math.floor(Math.random() * 3);
  const trades = [];
  // usedTeams resets each call — App.js calls this in a loop for batch generation
  const usedTeams = new Set();

  for (let attempt = 0; attempt < baseTrades * 12 && trades.length < baseTrades; attempt++) {
    const idx1 = Math.floor(Math.random() * TEAMS.length);
    let idx2 = Math.floor(Math.random() * TEAMS.length);
    if (idx2 === idx1) continue;
    const t1 = TEAMS[idx1], t2 = TEAMS[idx2];
    if (usedTeams.has(t1) || usedTeams.has(t2)) continue;
    // HARD GUARD: never trade user's team
    if (t1 === userTeam || t2 === userTeam) continue;

    const team1 = analyzeTeam(salaryData[t1], seasonYear);
    const team2 = analyzeTeam(salaryData[t2], seasonYear);
    if (team1.activePlayers.length < 6 || team2.activePlayers.length < 6) continue;

    const isBlockbuster = Math.random() < 0.08;
    const trade = buildTrade(t1, t2, team1, team2, isBlockbuster, seasonYear);
    if (trade) { trades.push(trade); usedTeams.add(t1); usedTeams.add(t2); }
  }
  return trades;
}

function buildTrade(t1Abbr, t2Abbr, team1, team2, isBlockbuster, seasonYear) {
  const maxOvr = isBlockbuster ? 99 : 84;
  const filterP = (team) => team.activePlayers
    .filter(p => { const o = getPlayerRating(p.name) || 70; return o >= 65 && o <= maxOvr && (p.salary2526 || 0) < 50e6; })
    .map(p => ({ ...p, ovr: getPlayerRating(p.name) || 70, value: getPlayerTradeValue(p, seasonYear) }))
    .sort((a, b) => b.value - a.value);

  const t1Pool = filterP(team1), t2Pool = filterP(team2);
  if (t1Pool.length < 1 || t2Pool.length < 1) return null;

  const tradeSize = isBlockbuster ? Math.min(2, t1Pool.length) : 1;
  const t1Sends = [];
  for (let i = 0; i < tradeSize && i < t1Pool.length; i++) {
    const pickIdx = isBlockbuster ? i : Math.min(i + 2, t1Pool.length - 1);
    if (pickIdx < t1Pool.length) t1Sends.push(t1Pool[pickIdx]);
  }
  if (t1Sends.length === 0) return null;

  const t1Val = t1Sends.reduce((s, p) => s + p.value, 0);
  const t1Sal = t1Sends.reduce((s, p) => s + (p.salary2526 || 0), 0);
  let bestMatch = null, bestDiff = Infinity;

  for (let count = 1; count <= (isBlockbuster ? 3 : 2); count++) {
    for (let i = 0; i < Math.min(t2Pool.length, 12); i++) {
      const combo = [t2Pool[i]];
      if (count >= 2 && i + 1 < t2Pool.length) combo.push(t2Pool[i + 1]);
      if (count >= 3 && i + 2 < t2Pool.length) combo.push(t2Pool[i + 2]);
      if (combo.length < count) continue;

      const cVal = combo.reduce((s, p) => s + p.value, 0);
      const cSal = combo.reduce((s, p) => s + (p.salary2526 || 0), 0);
      const vDiff = Math.abs(t1Val - cVal);
      if (vDiff > Math.max(5, t1Val * 0.25)) continue;
      if (!checkSalaryMatch(t1Sal, cSal, team1.payroll) || !checkSalaryMatch(cSal, t1Sal, team2.payroll)) continue;
      if (combo.length === 1 && t1Sends.length === 1 && Math.abs(combo[0].ovr - t1Sends[0].ovr) < 2) continue;
      if (vDiff < bestDiff) { bestDiff = vDiff; bestMatch = combo; }
    }
  }
  if (!bestMatch) return null;

  return {
    team1: t1Abbr, team2: t2Abbr,
    team1Sends: t1Sends.map(p => ({ name: p.name, salary: p.salary2526 || 0, ovr: p.ovr, value: Math.round(p.value) })),
    team2Sends: bestMatch.map(p => ({ name: p.name, salary: p.salary2526 || 0, ovr: p.ovr, value: Math.round(p.value) })),
    type: isBlockbuster ? 'blockbuster' : (t1Sends.length + bestMatch.length >= 3) ? 'medium' : 'small',
    t1Value: Math.round(t1Sends.reduce((s, p) => s + p.value, 0)),
    t2Value: Math.round(bestMatch.reduce((s, p) => s + p.value, 0)),
  };
}

// Counter offers: 1-2 per phase advance, targeting 5-10 per season
export function generateCounterOffers(salaryData, userTeam, seasonYear) {
  if (!userTeam || !salaryData[userTeam]) return [];
  const userA = analyzeTeam(salaryData[userTeam], seasonYear);
  if (userA.activePlayers.length < 8) return [];

  // Target 5-10 per season across ~3 phase transitions (~2 offers per phase)
  const roll = Math.random();
  const numOffers = roll < 0.10 ? 0 : roll < 0.35 ? 1 : roll < 0.70 ? 2 : 3;

  if (numOffers === 0) return [];

  const offers = [];
  const TEAMS = Object.keys(salaryData).filter(t => t !== userTeam && salaryData[t]?.players);

  for (let attempt = 0; attempt < numOffers * 6 && offers.length < numOffers; attempt++) {
    const otherAbbr = TEAMS[Math.floor(Math.random() * TEAMS.length)];
    if (offers.some(o => o.fromTeam === otherAbbr)) continue;
    const otherTeam = analyzeTeam(salaryData[otherAbbr], seasonYear);
    if (otherTeam.activePlayers.length < 8) continue;

    const userPlayers = userA.activePlayers
      .filter(p => { const o = getPlayerRating(p.name) || 70; return o >= 68 && o <= 83 && (p.salary2526 || 0) < 35e6; })
      .map(p => ({ ...p, ovr: getPlayerRating(p.name) || 70, value: getPlayerTradeValue(p, seasonYear) }));
    if (userPlayers.length === 0) continue;

    const target = userPlayers[Math.floor(Math.random() * userPlayers.length)];
    const overpayTarget = target.value * (1.05 + Math.random() * 0.1);

    const otherPlayers = otherTeam.activePlayers
      .filter(p => { const o = getPlayerRating(p.name) || 70; return o >= 65 && (p.salary2526 || 0) < 40e6; })
      .map(p => ({ ...p, ovr: getPlayerRating(p.name) || 70, value: getPlayerTradeValue(p, seasonYear) }));

    let bestCombo = null, bestDiff = Infinity;
    for (let i = 0; i < Math.min(otherPlayers.length, 8); i++) {
      for (const combo of [[otherPlayers[i]], ...(i + 1 < otherPlayers.length ? [[otherPlayers[i], otherPlayers[i + 1]]] : [])]) {
        const cVal = combo.reduce((s, p) => s + p.value, 0);
        const diff = Math.abs(cVal - overpayTarget);
        const cSal = combo.reduce((s, p) => s + (p.salary2526 || 0), 0);
        if (diff < bestDiff && diff < overpayTarget * 0.3 && checkSalaryMatch(target.salary2526 || 0, cSal, userA.payroll)) {
          bestDiff = diff; bestCombo = combo;
        }
      }
    }
    if (!bestCombo) continue;

    offers.push({
      id: `offer-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      fromTeam: otherAbbr,
      theyOffer: bestCombo.map(p => ({ name: p.name, salary: p.salary2526 || 0, ovr: p.ovr, value: Math.round(p.value) })),
      theyWant: [{ name: target.name, salary: target.salary2526 || 0, ovr: target.ovr, value: Math.round(target.value) }],
      theirValue: Math.round(bestCombo.reduce((s, p) => s + p.value, 0)),
      yourValue: Math.round(target.value),
      timestamp: Date.now(),
    });
  }
  return offers;
}

export function executeAITrades(trades, salaryData) {
  const executed = [];
  for (const trade of trades) {
    const t1 = salaryData[trade.team1], t2 = salaryData[trade.team2];
    if (!t1?.players || !t2?.players) continue;
    const t1Names = new Set(trade.team1Sends.map(p => p.name));
    const t2Names = new Set(trade.team2Sends.map(p => p.name));
    if (![...t1Names].every(n => t1.players.some(p => p.name === n))) continue;
    if (![...t2Names].every(n => t2.players.some(p => p.name === n))) continue;

    const t1Out = t1.players.filter(p => t1Names.has(p.name));
    const t2Out = t2.players.filter(p => t2Names.has(p.name));
    t1.players = [...t1.players.filter(p => !t1Names.has(p.name)), ...t2Out];
    t2.players = [...t2.players.filter(p => !t2Names.has(p.name)), ...t1Out];
    t1.payroll = t1.players.reduce((s, p) => s + (p.salary2526 || 0), 0);
    t2.payroll = t2.players.reduce((s, p) => s + (p.salary2526 || 0), 0);
    executed.push({ ...trade, timestamp: Date.now() });
  }
  return executed;
}

export function executeCounterOffer(offer, salaryData, userTeam) {
  const userT = salaryData[userTeam], otherT = salaryData[offer.fromTeam];
  if (!userT?.players || !otherT?.players) return false;
  const uNames = new Set(offer.theyWant.map(p => p.name));
  const oNames = new Set(offer.theyOffer.map(p => p.name));
  if (![...uNames].every(n => userT.players.some(p => p.name === n))) return false;
  if (![...oNames].every(n => otherT.players.some(p => p.name === n))) return false;

  const uOut = userT.players.filter(p => uNames.has(p.name));
  const oOut = otherT.players.filter(p => oNames.has(p.name));
  userT.players = [...userT.players.filter(p => !uNames.has(p.name)), ...oOut];
  otherT.players = [...otherT.players.filter(p => !oNames.has(p.name)), ...uOut];
  userT.payroll = userT.players.reduce((s, p) => s + (p.salary2526 || 0), 0);
  otherT.payroll = otherT.players.reduce((s, p) => s + (p.salary2526 || 0), 0);
  return true;
}

export default { generateAITrades, executeAITrades, generateCounterOffers, executeCounterOffer, getPlayerTradeValue, getPickTradeValue };
